import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static targets = [
    "recurrenceDialog", "recurrenceExplanation", "recurrenceDates",
    "teamsTab", "teamsPanel", "clubOne", "clubSearch", "clubId", "clubResults",
    "tenantSelect",
    "cancellationHours",
    "venueSelect",
    "sessionFormatSelect",
    "description",
    "allowHtml",
    "descriptionLabel",
    "descriptionPreviewButton",
    "descriptionPreviewDialog",
    "descriptionPreviewContent",
    "piratTrackSelect",
    "piratTrackLabel",
    "coachFeeWrapper",
    "coachFeeInput",
    "timeZoneNote",
    "privateBookingCodeWrapper",
    "privateBookingCodeInput",
    "pinCheckbox",
    "pinExpiryWrapper",
    "pinExpiryInput",
    "displayOnlyCheckbox",
    "displayOnlyColourWrapper",
    "displayOnlyColourInput",
    "displayOnlyColourPreview",
    "displayOnlyColourPreviewText",
    "helperToggleIntro",
    "helperToggleButton",
    "tabButton",
    "tabPanel",
    "tournamentCountrySelect",
    "tournamentStateWrapper",
    "tournamentStateSelect"
  ]

  connect() {
    this.updateDescriptionPreviewAvailability()
    if (this.hasVenueSelectTarget) {
      this.originalVenueOptions = Array.from(this.venueSelectTarget.options).map((option) => option.cloneNode(true))
      this.filterVenues()
    }
    this.updateTeams()
    this.updatePrivateBookingCode()
    this.updateCoachFee()
    this.updatePinExpiry()
    this.updateDisplayOnlyColour()
    this.updateDisplayOnlyColourPreview()
    this.updateTabButtons()
    this.updateTournamentState()
    this.updateExternalTournament()
  }

  explainWeeklyRecurrence(event) {
    const enabled = event.target.checked
    const originallyEnabled = event.target.dataset.originalRecurring === "true"
    if (enabled === originallyEnabled) return

    let dates
    if (enabled) {
      const value = this.element.querySelector("#session_starts_at_local")?.value
      if (!value) return
      const start = new Date(value)
      if (Number.isNaN(start.getTime())) return
      const now = new Date()
      const horizon = new Date(Math.max(now.getTime(), start.getTime()))
      horizon.setDate(horizon.getDate() + 56)
      horizon.setHours(23, 59, 59, 999)
      const date = new Date(start)
      date.setDate(date.getDate() + 7)
      dates = []
      while (date <= horizon) {
        if (date > now) dates.push(date.toLocaleString("en-AU", {
          weekday: "short", day: "numeric", month: "short", year: "numeric", hour: "numeric", minute: "2-digit"
        }))
        date.setDate(date.getDate() + 7)
      }
      this.recurrenceExplanationTarget.textContent = "Saving will schedule weekly sessions on the following dates, subject to court availability. Existing dates will not be duplicated."
    } else {
      dates = JSON.parse(this.recurrenceDatesTarget.dataset.hideSessions || "[]")
      this.recurrenceExplanationTarget.textContent = "Saving will stop weekly scheduling and hide the following future duplicated sessions without active bookings. Sessions with bookings will remain visible."
    }
    this.recurrenceDatesTarget.replaceChildren(...(dates.length ? dates : ["No affected future sessions."]).map((text) => {
      const item = document.createElement("li")
      item.textContent = text
      return item
    }))
    this.recurrenceDialogTarget.showModal()
  }

  closeRecurrenceExplanation() {
    this.recurrenceDialogTarget.close()
  }

  updateTeams() {
    if (!this.hasTeamsTabTarget) return
    const enabled = ["TwoTeams", "TwoTeamsInternal"].includes(this.sessionFormatSelectTarget.value)
    this.teamsTabTarget.classList.toggle("hidden", !enabled)
    this.teamsPanelTarget.querySelectorAll("input").forEach((input) => {
      if (input.dataset.sessionFormTarget === "clubOne" || input.dataset.sessionFormTarget === "clubSearch") return
      input.disabled = !enabled
    })
    if (!enabled && !this.teamsPanelTarget.classList.contains("hidden")) {
      const button = this.tabButtonTargets.find((b) => b.dataset.sessionFormTabValue === "session")
      if (button) this.selectTab({ preventDefault() {}, currentTarget: button })
    }
    this.updateClubOne()
  }

  updateClubOne() {
    if (this.hasClubOneTarget && this.tenantSelectTarget.tagName === "SELECT") {
      this.clubOneTarget.value = this.tenantSelectTarget.selectedOptions[0]?.dataset.tenantName || ""
    }
    if (!this.hasClubSearchTarget || !this.hasClubIdTarget) return
    const internal = this.sessionFormatSelectTarget.value === "TwoTeamsInternal"
    const locked = this.clubSearchTarget.dataset.locked === "true"
    this.clubSearchTarget.disabled = internal || locked
    if (internal) {
      this.clubIdTarget.value = this.tenantSelectTarget.value
      this.clubSearchTarget.value = this.clubOneTarget.value
      this.clubResultsTarget.replaceChildren()
      this.internalTeamSelected = true
    } else if (this.internalTeamSelected && !locked) {
      this.clubIdTarget.value = ""
      this.clubSearchTarget.value = ""
      this.clubResultsTarget.replaceChildren()
      this.internalTeamSelected = false
    }
  }

  async searchClubs() {
    this.clubIdTarget.value = ""
    this.clubResultsTarget.replaceChildren()
    this.clubSearchAbort?.abort()
    const query = this.clubSearchTarget.value.trim()
    if (query.length < 2) return
    this.clubSearchAbort = new AbortController()
    const url = new URL(this.clubSearchTarget.dataset.searchUrl, window.location.origin)
    url.searchParams.set("q", query)
    try {
      const response = await fetch(url, { signal: this.clubSearchAbort.signal, headers: { Accept: "application/json" } })
      if (!response.ok) return
      const clubs = await response.json()
      clubs.forEach((club) => {
        const button = document.createElement("button")
        button.type = "button"
        button.className = "block w-full px-3 py-2 text-left hover:bg-stone-100"
        button.textContent = club.name
        button.addEventListener("click", () => {
          this.clubIdTarget.value = club.id
          this.clubSearchTarget.value = club.name
          this.clubResultsTarget.replaceChildren()
        })
        this.clubResultsTarget.append(button)
      })
    } catch (error) {
      if (error.name !== "AbortError") this.clubResultsTarget.textContent = "Club search is unavailable. Please try again."
    }
  }

  disconnect() {
    this.clubSearchAbort?.abort()
  }

  toggleHelperTexts(event) {
    event.preventDefault()
    this.element.classList.toggle("session-form--helpers-hidden")
    this.updateHelperToggleText()
  }

  updateHelperToggleText() {
    if (!this.hasHelperToggleIntroTarget || !this.hasHelperToggleButtonTarget) return

    const helpersHidden = this.element.classList.contains("session-form--helpers-hidden")
    this.helperToggleIntroTarget.textContent = helpersHidden ? "For an explanation of all the fields," : "Extra help is visible."
    this.helperToggleButtonTarget.textContent = helpersHidden ? "show extra help" : "Hide extra help"
  }

  revealInvalidField(event) {
    // Validation runs before submit, including for fields in hidden tabs.
    // Reveal the first invalid field so the browser can focus it and explain why.
    const firstInvalid = event.target.form?.querySelector(":invalid")
    if (firstInvalid && firstInvalid !== event.target) return

    const panel = this.tabPanelTargets.find((panel) => panel.contains(event.target))
    if (!panel) return

    const button = this.tabButtonTargets.find((button) =>
      button.dataset.sessionFormTabValue === panel.dataset.sessionFormTabValue)
    if (button) this.selectTab({ preventDefault() {}, currentTarget: button })
  }

  selectTab(event) {
    event.preventDefault()
    const selectedTab = event.currentTarget.dataset.sessionFormTabValue
    if (!selectedTab) return

    this.tabPanelTargets.forEach((panel) => {
      panel.classList.toggle("hidden", panel.dataset.sessionFormTabValue !== selectedTab)
    })
    this.tabButtonTargets.forEach((button) => {
      const selected = button.dataset.sessionFormTabValue === selectedTab
      button.setAttribute("aria-selected", selected ? "true" : "false")
      button.classList.toggle("border-stone-900", selected)
      button.classList.toggle("bg-white", selected)
      button.classList.toggle("text-stone-950", selected)
      button.classList.toggle("border-transparent", !selected)
      button.classList.toggle("text-stone-600", !selected)
    })
  }

  updateTabButtons() {
    if (!this.hasTabButtonTarget) return

    this.tabButtonTargets.forEach((button) => {
      const selected = button.getAttribute("aria-selected") === "true"
      button.classList.toggle("border-stone-900", selected)
      button.classList.toggle("bg-white", selected)
      button.classList.toggle("text-stone-950", selected)
      button.classList.toggle("border-transparent", !selected)
      button.classList.toggle("text-stone-600", !selected)
    })
  }

  updateTournamentState() {
    if (!this.hasTournamentCountrySelectTarget || !this.hasTournamentStateWrapperTarget || !this.hasTournamentStateSelectTarget) return

    const isAustralia = this.tournamentCountrySelectTarget.value === "AU"
    this.tournamentStateWrapperTarget.classList.toggle("hidden", !isAustralia)
    this.tournamentStateSelectTarget.disabled = !isAustralia
    if (!isAustralia) this.tournamentStateSelectTarget.value = ""
  }

  cancellationHoursChanged() {
    this.cancellationHoursEdited = true
  }

  updateCancellationDefault() {
    if (!this.hasCancellationHoursTarget || this.cancellationHoursEdited) return
    const input = this.cancellationHoursTarget
    if (input.dataset.newSession !== "true" || input.value !== input.dataset.tenantDefault) return
    const hours = this.tenantSelectTarget.selectedOptions[0]?.dataset.cancellationHours
    if (hours === undefined) return
    input.value = hours
    input.dataset.tenantDefault = hours
  }

  filterVenues() {
    this.updateClubOne()
    const tenantId = this.tenantSelectTarget.value
    const selectedVenueId = this.venueSelectTarget.value
    const matchingOptions = this.originalVenueOptions.filter((option) => {
      return option.value === "" || option.dataset.tenantId === tenantId
    })

    this.venueSelectTarget.replaceChildren(...matchingOptions.map((option) => option.cloneNode(true)))

    const selectedOptionStillAvailable = Array.from(this.venueSelectTarget.options).some((option) => {
      return option.value !== "" && option.value === selectedVenueId
    })

    if (selectedOptionStillAvailable) {
      this.venueSelectTarget.value = selectedVenueId
      this.updateTimeZoneNotes()
      return
    }

    const firstVenueOption = Array.from(this.venueSelectTarget.options).find((option) => option.value !== "")
    this.venueSelectTarget.value = firstVenueOption ? firstVenueOption.value : ""
    this.updateTimeZoneNotes()
  }

  updateDescriptionPreviewAvailability() {
    const enabled = this.hasAllowHtmlTarget && this.allowHtmlTarget.checked
    if (this.hasDescriptionLabelTarget) this.descriptionLabelTarget.classList.toggle("hidden", enabled)
    if (this.hasDescriptionPreviewButtonTarget) this.descriptionPreviewButtonTarget.classList.toggle("hidden", !enabled)
  }

  async previewDescription(event) {
    event.preventDefault()
    if (!this.hasAllowHtmlTarget || !this.allowHtmlTarget.checked) return
    this.descriptionPreviewContentTarget.textContent = "Loading preview…"
    this.descriptionPreviewDialogTarget.showModal()
    try {
      const response = await fetch(this.descriptionPreviewButtonTarget.dataset.previewUrl, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Accept: "application/json",
          "X-CSRF-Token": document.querySelector('meta[name="csrf-token"]')?.content || ""
        },
        body: JSON.stringify({ description: this.descriptionTarget.value })
      })
      if (!response.ok) throw new Error("Preview failed")
      const result = await response.json()
      // The server uses the same HTML sanitizer as the session details page.
      if (result.html) this.descriptionPreviewContentTarget.innerHTML = result.html
      else this.descriptionPreviewContentTarget.textContent = "The description is empty."
    } catch {
      this.descriptionPreviewContentTarget.textContent = "Unable to load the preview. Please try again."
    }
  }

  closeDescriptionPreview() {
    this.descriptionPreviewDialogTarget.close()
  }

  closeDescriptionPreviewBackdrop(event) {
    if (event.target !== this.descriptionPreviewDialogTarget) return
    const bounds = this.descriptionPreviewDialogTarget.getBoundingClientRect()
    if (event.clientX < bounds.left || event.clientX > bounds.right || event.clientY < bounds.top || event.clientY > bounds.bottom) {
      this.closeDescriptionPreview()
    }
  }

  updateDescription() {
    if (!this.hasDescriptionTarget) return
    if (this.descriptionTarget.dataset.newSession !== "true" && this.descriptionTarget.value.trim() !== "") return
    if (!this.hasSessionFormatSelectTarget || !this.sessionFormatSelectTarget.value) return

    this.populateFormatDescription()
  }

  updateDescriptionFormatting() {
    if (!this.hasDescriptionTarget || !this.hasSessionFormatSelectTarget) return
    const option = this.sessionFormatSelectTarget.selectedOptions[0]
    if (!option || !this.descriptionTarget.value) return
    if (![option.dataset.description, option.dataset.htmlDescription].includes(this.descriptionTarget.value)) return

    this.populateFormatDescription()
  }

  populateFormatDescription() {
    const option = this.sessionFormatSelectTarget.selectedOptions[0]
    this.descriptionTarget.value = (this.hasAllowHtmlTarget && this.allowHtmlTarget.checked
      ? option?.dataset.htmlDescription
      : option?.dataset.description) || ""
    this.descriptionTarget.dispatchEvent(new Event("input", { bubbles: true }))
  }

  updatePiratTrack() {
    this.updateExternalTournament()
    if (!this.hasSessionFormatSelectTarget || !this.hasPiratTrackSelectTarget) return

    const selectedOption = this.sessionFormatSelectTarget.selectedOptions[0]
    if (!selectedOption) return

    const selectedLabel = selectedOption.textContent.toLowerCase()
    const selectedValue = selectedOption.value

    if (selectedValue.endsWith("_SKINNY")) {
      this.setPiratTrack("skinny_singles")
    } else if (selectedLabel.includes("singles")) {
      this.setPiratTrack("singles")
    } else if (this.piratTrackSelectTarget.tagName === "INPUT") {
      this.setPiratTrack("doubles")
    }

    this.updateTeams()
    this.updatePrivateBookingCode()
    this.updateCoachFee()
    this.updateExternalTournament()
  }

  updateExternalTournament() {
    const external = this.hasSessionFormatSelectTarget && this.sessionFormatSelectTarget.value === "External Tournament"
    this.element.dataset.externalTournament = String(external)
    this.internalControlStates ||= new Map()
    this.element.querySelectorAll("[data-session-form-internal] input, [data-session-form-internal] select, [data-session-form-internal] textarea").forEach((input) => {
      if (external) {
        if (!this.internalControlStates.has(input)) this.internalControlStates.set(input, input.disabled)
        input.disabled = true
      } else if (this.internalControlStates.has(input)) {
        input.disabled = this.internalControlStates.get(input)
        this.internalControlStates.delete(input)
      }
    })
    this.element.querySelectorAll("[data-session-form-external] input").forEach((input) => {
      input.disabled = !external
      input.required = external
    })
    if (external) {
      const selected = this.tabButtonTargets.find((button) => button.getAttribute("aria-selected") === "true")
      if (["booking", "tournament"].includes(selected?.dataset.sessionFormTabValue)) {
        const sessionTab = this.tabButtonTargets.find((button) => button.dataset.sessionFormTabValue === "session")
        if (sessionTab) this.selectTab({ preventDefault() {}, currentTarget: sessionTab })
      }
    }
    this.updateDisplayOnlyColour()
  }

  setPiratTrack(value) {
    this.piratTrackSelectTarget.value = value

    if (this.hasPiratTrackLabelTarget) {
      this.piratTrackLabelTarget.textContent = value.split("_").map((word) => {
        return word.charAt(0).toUpperCase() + word.slice(1)
      }).join(" ")
    }
  }

  updateTimeZoneNotes() {
    if (!this.hasTimeZoneNoteTarget) return

    const selectedOption = this.venueSelectTarget.selectedOptions[0]
    const timeZone = selectedOption?.dataset.timeZone
    if (!timeZone) return

    this.timeZoneNoteTargets.forEach((target) => {
      target.textContent = `${target.dataset.prefix || ""}${timeZone}${target.dataset.suffix || ""}`
    })
  }

  updatePrivateBookingCode() {
    if (!this.hasPrivateBookingCodeWrapperTarget || !this.hasPrivateBookingCodeInputTarget || !this.hasSessionFormatSelectTarget) return

    const isPrivateBooking = ["Private", "Pickle Patch"].includes(this.sessionFormatSelectTarget.value)
    this.privateBookingCodeWrapperTarget.classList.toggle("hidden", !isPrivateBooking)
    this.privateBookingCodeInputTarget.disabled = !isPrivateBooking
  }

  updateCoachFee() {
    if (!this.hasCoachFeeWrapperTarget || !this.hasCoachFeeInputTarget || !this.hasSessionFormatSelectTarget) return

    const coachFeeAvailable = ["Coaching", "I_Intro"].includes(this.sessionFormatSelectTarget.value)
    this.coachFeeWrapperTarget.classList.toggle("hidden", !coachFeeAvailable)
    this.coachFeeInputTarget.disabled = !coachFeeAvailable
  }

  updatePinExpiry() {
    if (!this.hasPinCheckboxTarget || !this.hasPinExpiryWrapperTarget || !this.hasPinExpiryInputTarget) return

    const isPinned = this.pinCheckboxTarget.checked
    this.pinExpiryWrapperTarget.classList.toggle("hidden", !isPinned)
    this.pinExpiryInputTarget.disabled = !isPinned
  }

  updateDisplayOnlyColour() {
    if (!this.hasDisplayOnlyCheckboxTarget || !this.hasDisplayOnlyColourWrapperTarget || !this.hasDisplayOnlyColourInputTarget) return

    const isDisplayOnly = this.element.dataset.externalTournament === "true" || this.displayOnlyCheckboxTarget.checked
    this.displayOnlyColourWrapperTarget.classList.toggle("hidden", !isDisplayOnly)
    this.displayOnlyColourInputTarget.disabled = !isDisplayOnly
  }

  updateDisplayOnlyColourPreview() {
    if (!this.hasDisplayOnlyColourInputTarget || !this.hasDisplayOnlyColourPreviewTarget) return

    const [foreground, background, border] = this.displayOnlyColourInputTarget.value
      .split(",")
      .map((part) => this.normalizedHexColour(part))
    const preview = this.displayOnlyColourPreviewTarget
    preview.style.color = `#${foreground || "ffffff"}`
    preview.style.backgroundColor = `#${background || "e83283"}`
    preview.style.borderColor = `#${border || "ffffff"}`
  }

  normalizedHexColour(value) {
    const colour = (value || "").trim().replace(/^#/, "")
    return /^[0-9a-fA-F]{6}$/.test(colour) ? colour : null
  }
}
